"use client";

import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

import ScreenLayout from "@/components/layout/screen-layout";
import BodyLineText from "@/components/common/text/bodyline-text";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getYoutubeEmbedSrc,
  HOME_CLIENT_VIDEO_REVIEW,
} from "@/lib/dto/home/client-video-review-data";
import { ImgTestimonialCasaFriends, LogoAtMyCasa } from "@/lib/images";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

function ClientVideoReviewSection() {
  const [videoOpen, setVideoOpen] = useState(false);
  const data = HOME_CLIENT_VIDEO_REVIEW;
  const motionProps = {
    initial: "hidden" as const,
    whileInView: "visible" as const,
    viewport: { once: true, amount: 0.12 },
  };

  return (
    <section
      className="py-14 sm:py-16 md:py-20 lg:py-28"
      aria-labelledby="client-review-heading"
    >
      <ScreenLayout>
        <motion.div
          className="mx-auto mb-10 w-full text-center sm:mb-12 md:mb-14"
          variants={fadeUp}
          custom={0}
          {...motionProps}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#5231E7] sm:text-sm">
            {data.eyebrow}
          </p>

          <h2
            id="client-review-heading"
            className="mt-4 text-3xl font-semibold leading-[1.15] text-[#000929] sm:mt-5 sm:text-4xl md:text-5xl lg:text-[52px]"
          >
            {data.title}{" "}
            <span className="bg-gradient-to-r from-[#5231E7] to-[#764AF1] bg-clip-text text-transparent">
              {data.titleAccent}
            </span>
          </h2>

          <BodyLineText wrapperClassName="mx-auto lg:max-w-[80%]">
            {data.subtitle}
          </BodyLineText>
        </motion.div>

        <motion.div
          className="mx-auto max-w-[1140px] rounded-[24px] border border-[#000929]/12 bg-white p-5 shadow-[0_20px_60px_-32px_rgba(0,9,41,0.18)] sm:p-7 md:p-8 lg:p-9"
          variants={fadeUp}
          custom={0.08}
          {...motionProps}
        >
          <div className="grid grid-cols-1 items-stretch gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-10 xl:gap-12">
            {/* Video thumbnail */}
            <div className="flex min-h-0 items-center">
              <div className="relative w-full">
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  className="group relative block aspect-video w-full cursor-pointer overflow-hidden rounded-2xl bg-black ring-1 ring-black/5 transition-shadow hover:shadow-[0_20px_50px_-20px_rgba(82,49,231,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5231E7] focus-visible:ring-offset-2"
                  aria-label="Play AtMyCasa client review video"
                >
                  <Image
                    src={ImgTestimonialCasaFriends}
                    alt="AtMyCasa client review video preview"
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                    sizes="(max-width: 1024px) 100vw, 560px"
                  />
                  <div className="absolute inset-0 bg-black/10 transition-colors group-hover:bg-black/20" />
                </button>

                {/* Play control — bottom-right overlap like reference */}
                <button
                  type="button"
                  onClick={() => setVideoOpen(true)}
                  className="absolute -bottom-3 -right-2 flex size-12 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[0_8px_24px_rgba(82,49,231,0.35)] transition-transform hover:scale-105 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:-bottom-4 sm:-right-3 sm:size-14"
                  aria-label="Play video"
                >
                  <Play className="ml-0.5 size-5 fill-current sm:size-6" aria-hidden />
                </button>
              </div>
            </div>

            {/* Quote */}
            <div className="flex min-h-0 flex-col justify-center">
              <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5231E7] sm:text-xs">
                {data.category}
              </p>

              <blockquote className="space-y-3">
                <p className="text-base font-bold leading-snug text-[#000929] sm:text-lg md:leading-relaxed">
                  &ldquo;{data.quote}&rdquo;
                </p>
                <p className="text-sm leading-relaxed text-[#000929]/70 sm:text-[15px]">
                  {data.supportingLine}
                </p>
              </blockquote>

              <div className="mt-5 border-t border-[#000929]/8 pt-5 sm:mt-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <Image
                      src={LogoAtMyCasa}
                      alt="AtMyCasa logo"
                      className="h-8 w-auto shrink-0 object-contain sm:h-9 md:h-10"
                    />
                    <div>
                      <p className="text-base font-bold text-[#000929] sm:text-lg">
                        {data.author.name}
                      </p>
                      <p className="mt-0.5 text-sm text-[#000929]/55">
                        {data.author.role}
                      </p>
                    </div>
                  </div>

                  <Link
                    href={data.caseStudyHref}
                    className="group inline-flex items-center gap-1.5 text-sm font-semibold text-[#5231E7] transition-colors hover:text-[#764AF1] sm:text-base"
                  >
                    {data.caseStudyLabel}
                    <ArrowUpRight
                      className="size-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </ScreenLayout>

      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent
          className="max-w-[min(96vw,960px)] gap-0 overflow-hidden border-0 bg-black p-0 sm:max-w-[min(92vw,960px)]"
          overlayClassName="bg-black/80"
          showCloseButton
        >
          <DialogTitle className="sr-only">
            AtMyCasa client review with Devkrest
          </DialogTitle>
          <div className="relative aspect-video w-full bg-black">
            {videoOpen ? (
              <iframe
                className="absolute inset-0 h-full w-full"
                src={getYoutubeEmbedSrc(data.youtubeVideoId, true)}
                title="AtMyCasa client review with Devkrest"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default ClientVideoReviewSection;
